// The audit log, Section 8. Admin only: every write in the system records
// who did it, to what, and the before and after, and this page is where that
// trail is read.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listAuditLog } from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import { ButtonLink, EmptyState, Notice, Panel, Pill, Select } from '../components/ui/index.js'
import { auditActionTone } from '../tones/audit.js'
import { formatInstant } from '../vocab.js'

const ACTION_LABEL = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Removed',
  STATUS_CHANGE: 'Status change',
}

// Entity names are table names; the log reads better with plain words.
const entityLabel = (entity) => entity.replaceAll('_', ' ')

function Diff({ entry }) {
  if (!entry.before && !entry.after) return null
  return (
    <details className="row-diff">
      <summary className="muted">What changed</summary>
      <pre>
        {entry.before ? `before: ${JSON.stringify(entry.before, null, 1)}\n` : ''}
        {entry.after ? `after: ${JSON.stringify(entry.after, null, 1)}` : ''}
      </pre>
    </details>
  )
}

export default function AuditLog() {
  const { can, homePath } = useAuth()
  const [entity, setEntity] = useState('')
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    try {
      setData(await listAuditLog(entity || null))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [entity])

  useEffect(() => {
    if (can(ACTION.AUDIT_READ)) load()
  }, [load, can])

  if (!can(ACTION.AUDIT_READ)) {
    return (
      <Notice tone="bad">
        The audit log is admin only. <Link to={homePath}>Go back</Link>.
      </Notice>
    )
  }
  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }

  return (
    <>
      <div className="toolbar">
        <ButtonLink to="/reports">Back to reports</ButtonLink>
        <Select
          value={entity}
          onChange={setEntity}
          ariaLabel="Filter by record type"
          style={{ width: '16rem' }}
          options={[
            { value: '', label: 'Every record type' },
            ...(data?.entities ?? []).map((name) => ({ value: name, label: entityLabel(name) })),
          ]}
        />
        <span className="spacer" />
      </div>

      <Panel
        title="Audit log"
        support={data ? `The most recent ${data.entries.length} entries, newest first` : 'Loading'}
      >
        {data && data.entries.length === 0 ? (
          <EmptyState title="Nothing recorded">
            Every write lands here with who did it and what changed.
          </EmptyState>
        ) : null}

        {data && data.entries.length > 0 ? (
          <div className="rows">
            {data.entries.map((entry) => (
              <div key={entry.id} className="row">
                <div className="row-main">
                  <div className="row-primary wraps">
                    {entry.actorName} {ACTION_LABEL[entry.action]?.toLowerCase() ?? entry.action}{' '}
                    {entityLabel(entry.entity)}
                  </div>
                  <div className="row-meta">
                    {formatInstant(entry.createdAt)}
                    {entry.entityId ? ` · ${entry.entityId}` : ''}
                  </div>
                  <Diff entry={entry} />
                </div>
                <div className="row-trail">
                  <Pill tone={auditActionTone[entry.action] ?? 'neutral'}>
                    {ACTION_LABEL[entry.action] ?? entry.action}
                  </Pill>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Panel>
    </>
  )
}
